import * as React from "react";
import { ScrollArea as ScrollAreaPrimitive } from "@base-ui/react/scroll-area";

import { cn } from "../../lib/utils";

/**
 * Migrated from `@radix-ui/react-scroll-area` to Base UI.
 *
 * 41 files consume this and almost none of them pass a prop, so the prop
 * surface was never the risk. Four differences reach behaviour, and three are
 * absorbed here so no call site changes.
 *
 *  1. **`type` has no Base UI equivalent.** Radix defaults it to `"hover"` and
 *     genuinely unmounts the scrollbar until the pointer arrives; Base UI
 *     always renders it once the axis overflows. Left alone that is a new,
 *     permanently visible bar in 41 places. The prop is kept and mapped onto
 *     Base UI's `data-hovering` / `data-scrolling` attributes instead —
 *     `SCROLLBAR_VISIBILITY` below. One caller passes `type="auto"`
 *     (`WorkflowEditorPanel`) and keeps working unchanged.
 *  2. **Base UI scrolls both axes.** Radix derives the viewport's overflow from
 *     which scrollbars are mounted, and this component mounts only a vertical
 *     one, so horizontal content is clipped. `overflow-x-hidden` keeps that:
 *     un-clipping it would hand 41 areas a sideways trackpad gesture they have
 *     never had, in dialogs and chat panels where the content is often a few
 *     pixels wider than its box.
 *  3. **A `Content` part.** Radix wrapped children in an unnamed
 *     `min-width:100%` div; Base UI names the same wrapper and sets
 *     `min-width:fit-content`. It is kept so the depth of the tree — which
 *     `closest()` walks — is unchanged.
 *
 * The fourth is absorbed too, but deliberately and against the library — see
 * `VIEWPORT_NOT_A_TAB_STOP` below.
 */

/**
 * Radix's four `type` values collapse to two questions Base UI can answer from
 * the scrollbar's own state attributes: is the pointer over the area, and is it
 * being scrolled.
 *
 * `auto` needs nothing further — "show it whenever the axis overflows" is
 * exactly when Base UI mounts the scrollbar at all. `always` is the one value
 * opacity cannot express: Radix renders that bar even with nothing to scroll,
 * and Base UI's `keepMounted` defaults to `false`, so the element is gone
 * before any class applies. It gets `keepMounted` at the call site below.
 */
type ScrollAreaType = "auto" | "always" | "scroll" | "hover";

const SCROLLBAR_VISIBILITY: Record<ScrollAreaType, string> = {
  auto: "opacity-100",
  always: "opacity-100",
  hover:
    "pointer-events-none opacity-0 data-[hovering]:pointer-events-auto data-[hovering]:opacity-100 data-[scrolling]:pointer-events-auto data-[scrolling]:opacity-100",
  scroll:
    "pointer-events-none opacity-0 data-[scrolling]:pointer-events-auto data-[scrolling]:opacity-100",
};

/** Module scope so it is not a fresh object on every render. */
const VIEWPORT_CLIP: React.CSSProperties = { overflowX: "hidden" };

/**
 * Base UI sets `tabIndex: 0` on the viewport whenever either axis can scroll
 * (`ScrollAreaViewport.mjs`, the `hiddenState.x && hiddenState.y ? -1 : 0`
 * line). Radix set no `tabindex` at all, so adopting Base UI's default would
 * add a tab stop to all 41 scroll areas at once and start rendering the
 * `focus-visible:ring` classes this component has always carried but never
 * shown. We hold Radix's behaviour for now; `-1` keeps the element
 * programmatically focusable while leaving it out of the tab order.
 *
 * **Base UI's default is the accessible one, and this is a deliberate step
 * away from it.** A scroll container that a keyboard user cannot focus fails
 * WCAG 2.1.1 Keyboard (Level A) — it is axe-core's `scrollable-region-
 * focusable` rule — because there is no way to reach content that only
 * scrolling reveals.
 *
 * What makes deferring defensible rather than a blanket dismissal is that the
 * criterion only bites when the region's content is not otherwise
 * keyboard-reachable. Most consumers here wrap lists of buttons, links or
 * inputs, and tabbing to those scrolls them into view — the content is already
 * reachable, and the extra stop is redundant. The ones genuinely failing today
 * are the read-only text panels with nothing focusable inside:
 * `SchemaVersionPreviewDialog` is the clearest, a `max-h-[65vh]` box of plain
 * prose with zero interactive children.
 *
 * So this is a scoping decision, not a verdict: fixing it belongs with those
 * specific panels, where the stop can be given an accessible name and a
 * `role="region"` instead of appearing unlabelled in 41 places. Delete this
 * constant and its `tabIndex` below to adopt Base UI's default wholesale.
 */
const VIEWPORT_NOT_A_TAB_STOP = -1;

type ScrollAreaProps = Omit<
  ScrollAreaPrimitive.Root.Props,
  "className" | "style"
> & {
  className?: string;
  style?: React.CSSProperties;
  /** @default "hover" — the same default Radix applied. */
  type?: ScrollAreaType;
};

function ScrollArea({
  className,
  children,
  type = "hover",
  ...props
}: ScrollAreaProps) {
  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      className={cn("relative min-w-0", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        data-slot="scroll-area-viewport"
        // Base UI writes `overflow: scroll` as an inline style, so the clip has
        // to be an inline style too — a Tailwind class loses to it, and
        // `!important` would win by fighting the library rather than composing
        // with it. Base UI merges a caller's `style` over its own.
        //
        // `max-h-[inherit]` makes a `max-h-*` (or `style.maxHeight`) on the
        // root actually cap the list. `size-full` is `height: 100%`, which
        // only resolves against an explicit parent height, so a max-height
        // root otherwise grows with its content and paints over whatever
        // sits under it (dialog footers).
        style={VIEWPORT_CLIP}
        tabIndex={VIEWPORT_NOT_A_TAB_STOP}
        className="focus-visible:ring-ring/50 size-full max-h-[inherit] min-w-0 rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1"
      >
        <ScrollAreaPrimitive.Content data-slot="scroll-area-content">
          {children}
        </ScrollAreaPrimitive.Content>
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar
        className={SCROLLBAR_VISIBILITY[type]}
        keepMounted={type === "always"}
      />
      <ScrollAreaPrimitive.Corner data-slot="scroll-area-corner" />
    </ScrollAreaPrimitive.Root>
  );
}

type ScrollBarProps = Omit<
  ScrollAreaPrimitive.Scrollbar.Props,
  "className" | "style"
> & {
  className?: string;
  style?: React.CSSProperties;
};

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: ScrollBarProps) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none p-px transition-[color,opacity] select-none",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent",
        className,
      )}
      {...props}
    >
      <ScrollAreaPrimitive.Thumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-full"
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}

export { ScrollArea, ScrollBar };
